import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, isRef as _isRef, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createSlots as _createSlots, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "ocr-verification-products-table__catalogue-cell" }
const _hoisted_2 = { class: "ocr-verification-products-table__details" }
const _hoisted_3 = { class: "ocr-verification-products-table__details__top" }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { class: "typographies-paragraph-small-regular" }

import {
    computed,
    inject,
    nextTick,
    onBeforeUnmount,
    ref,
    watch,
    type ComputedRef,
    type Ref,
} from 'vue';
import { useField, useSubmitCount } from 'vee-validate';
import { search } from 'api/searchEngineService';
import {
    limitToTwoDecimals,
    PRECISION_FACTOR,
    AMOUNT_DECIMAL_PLACES,
} from 'composables/useOcrProducts';
import { useTax } from 'composables/useTax';
import Item from 'api/item';
import type { OcrProduct } from 'composables/ocr/ocrFormSchema';
import type { TaxRate } from 'types/entities/accounting/taxRate.type';
import type { AccountingCode } from 'types/entities/accounting/accountingCode.type';
import type { Unit } from 'types/entities/units/unit.type';
import type { Warehouse } from 'types/entities/stock/warehouse.type';
import type { PushToast } from 'types/injects/pushToast.type';
import type { Translator } from 'types/injects/translator.type';
import {
    CatalogueItemEvent,
    type CatalogueItemUpdatedPayload,
} from 'types/events/catalogueItem.events';

declare global {
    interface Window {
        catalogueItem: {
            edit: (
                type: string,
                id: number | undefined,
                arg3: boolean,
                arg4: string,
                options: { preventRelocate: boolean; hideAddAnother: boolean },
            ) => void;
        };
    }
}

interface Props {
    index: number;
    disabled?: boolean;
    canExpand: boolean;
    isExpanded: boolean;
    canDelete: boolean;
    taxRates: TaxRate[];
    accountingCodes: AccountingCode[];
    units?: Unit[];
    warehouses?: Warehouse[];
    showAnalyticCode?: boolean;
    isLineItemsEnabled?: boolean;
    currencySymbol?: string;
    formatDateDisplayed?: string;
}

interface SearchResult {
    name?: string;
    number?: string;
    object: { id: number; type: string };
}

interface ItemApiData {
    name: string;
    accounting_purchase_code_id: number | null;
    type: string;
}

interface ItemApiResponse {
    data: ItemApiData;
}

type CatalogueItemUpdateHandler = (payload: CatalogueItemUpdatedPayload) => void;

export default /*@__PURE__*/_defineComponent({
  __name: 'OcrVerificationProductsTableRow',
  props: {
    index: {},
    disabled: { type: Boolean, default: false },
    canExpand: { type: Boolean },
    isExpanded: { type: Boolean },
    canDelete: { type: Boolean },
    taxRates: {},
    accountingCodes: {},
    units: { default: () => [] },
    warehouses: { default: () => [] },
    showAnalyticCode: { type: Boolean, default: false },
    isLineItemsEnabled: { type: Boolean, default: false },
    currencySymbol: { default: undefined },
    formatDateDisplayed: { default: undefined }
  },
  emits: ["accounting-code-update", "catalogue-link-update", "toggle-expand", "request-delete", "request-duplicate", "product-updated"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;
const { taxLabel, isEinvoicingCompliant } = useTax();

const submitCount = useSubmitCount();
const fieldOptions = { validateOnValueUpdate: false };
const fieldPath = (name: string) => (): string => `products[${props.index}].${name}`;

// Errors are only displayed once the user has attempted to submit at least once.
const gateError = (message: Ref<string | undefined>): ComputedRef<string> =>
    computed(() => (submitCount.value > 0 ? (message.value ?? '') : ''));

const { value: description, errorMessage: descriptionMessage } = useField<string>(
    fieldPath('description'),
    undefined,
    fieldOptions,
);
const descriptionError = gateError(descriptionMessage);

const { value: quantity } = useField<number | string | null>(
    fieldPath('quantity'),
    undefined,
    fieldOptions,
);
const { value: priceWithoutTax, errorMessage: priceWithoutTaxMessage } = useField<
    number | string | null
>(fieldPath('priceWithoutTax'), undefined, fieldOptions);
const priceWithoutTaxError = gateError(priceWithoutTaxMessage);

const { value: taxRateId, errorMessage: taxRateIdMessage } = useField<number | null>(
    fieldPath('taxRateId'),
    undefined,
    fieldOptions,
);
const taxRateIdError = gateError(taxRateIdMessage);
const { value: unit } = useField<string | number | null>(
    fieldPath('unit'),
    undefined,
    fieldOptions,
);
const { value: stockOutDate } = useField<string | null>(
    fieldPath('stockOutDate'),
    undefined,
    fieldOptions,
);
const { value: warehouseId } = useField<string | number | null>(
    fieldPath('warehouseId'),
    undefined,
    fieldOptions,
);
const { value: serialNumbers } = useField<string | null>(
    fieldPath('serialNumbers'),
    undefined,
    fieldOptions,
);
const { value: taxAmountOverride } = useField<number | string | null>(
    fieldPath('taxAmountOverride'),
    undefined,
    fieldOptions,
);
const { value: amountIncludingTaxOverride } = useField<number | string | null>(
    fieldPath('amountIncludingTaxOverride'),
    undefined,
    fieldOptions,
);
const { value: accountingCode, errorMessage: accountingCodeMessage } = useField<number | null>(
    fieldPath('accountingCode'),
    undefined,
    fieldOptions,
);
const accountingCodeError = gateError(accountingCodeMessage);
const { value: analyticCode } = useField<string | null>(
    fieldPath('analyticCode'),
    undefined,
    fieldOptions,
);
const { value: catalogueLink } = useField<OcrProduct['catalogueLink']>(
    fieldPath('catalogueLink'),
    undefined,
    fieldOptions,
);
const { value: related } = useField<OcrProduct['related']>(
    fieldPath('related'),
    undefined,
    fieldOptions,
);

const compliantTaxRates = computed<TaxRate[]>(() =>
    props.taxRates.filter((tax) => isEinvoicingCompliant(tax)),
);
const showAnalyticCodeColumn = computed<boolean>(
    () => Boolean(props.showAnalyticCode) && Boolean(props.isLineItemsEnabled),
);

const totalWithoutTaxInCents = computed<number | null>(() => {
    const price = parseFloat(String(priceWithoutTax.value ?? '').replace(',', '.'));
    const qty = parseFloat(String(quantity.value ?? '')) || 1;
    if (Number.isNaN(price)) {
        return null;
    }
    return Math.round(price * qty * PRECISION_FACTOR);
});

const productTaxInCents = computed<number | null>(() => {
    const withoutTaxInCents = totalWithoutTaxInCents.value;
    const taxItem = props.taxRates.find((item) => item.id === taxRateId.value);
    if (withoutTaxInCents === null || !taxItem) {
        return null;
    }
    return Math.round((withoutTaxInCents * Number(taxItem.rate)) / 100);
});

const computedTaxAmount = computed<string | null>(() => {
    if (productTaxInCents.value === null) {
        return null;
    }
    return (productTaxInCents.value / PRECISION_FACTOR)
        .toFixed(AMOUNT_DECIMAL_PLACES)
        .replace('.', ',');
});

const computedAmountIncludingTax = computed<string | null>(() => {
    if (productTaxInCents.value === null || totalWithoutTaxInCents.value === null) {
        return null;
    }
    return ((totalWithoutTaxInCents.value + productTaxInCents.value) / PRECISION_FACTOR)
        .toFixed(AMOUNT_DECIMAL_PLACES)
        .replace('.', ',');
});

const taxAmount = computed<number | string | null>(
    () => taxAmountOverride.value ?? computedTaxAmount.value,
);
const amountIncludingTax = computed<number | string | null>(
    () => amountIncludingTaxOverride.value ?? computedAmountIncludingTax.value,
);

const isSerializedStock = computed<boolean>(() => related.value?.serialized_stock === 'Y');

watch([priceWithoutTax, quantity, taxRateId], () => {
    taxAmountOverride.value = null;
    amountIncludingTaxOverride.value = null;
});

const onPriceInput = (value: string | number | null): void => {
    priceWithoutTax.value = limitToTwoDecimals(value);
};

const formatRefToTwoDecimals = (fieldRef: Ref<number | string | null>): void => {
    const value = fieldRef.value;
    if (value === null || value === '') {
        return;
    }
    const parsed = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
    if (!Number.isNaN(parsed)) {
        fieldRef.value = parsed.toFixed(AMOUNT_DECIMAL_PLACES).replace('.', ',');
    }
};

const formatPriceOnBlur = (): void => formatRefToTwoDecimals(priceWithoutTax);
const formatTaxAmountOverrideOnBlur = (): void => formatRefToTwoDecimals(taxAmountOverride);
const formatAmountIncludingTaxOverrideOnBlur = (): void =>
    formatRefToTwoDecimals(amountIncludingTaxOverride);

const formatCentsAsAmount = (cents: number): string =>
    (cents / PRECISION_FACTOR).toFixed(AMOUNT_DECIMAL_PLACES).replace('.', ',');

const parseAmountToCents = (value: string | number | null): number | null => {
    const parsed = parseFloat(String(value ?? '').replace(',', '.'));
    return Number.isNaN(parsed) ? null : Math.round(parsed * PRECISION_FACTOR);
};

const onTaxAmountInput = (value: string | number | null): void => {
    const limited = limitToTwoDecimals(value);
    taxAmountOverride.value = limited;
    const withoutTaxInCents = totalWithoutTaxInCents.value;
    const taxInCents = parseAmountToCents(limited);
    if (withoutTaxInCents !== null && taxInCents !== null) {
        amountIncludingTaxOverride.value = formatCentsAsAmount(withoutTaxInCents + taxInCents);
    }
};

const onAmountIncludingTaxInput = (value: string | number | null): void => {
    const limited = limitToTwoDecimals(value);
    amountIncludingTaxOverride.value = limited;
    const withoutTaxInCents = totalWithoutTaxInCents.value;
    const totalWithTaxInCents = parseAmountToCents(limited);
    if (withoutTaxInCents !== null && totalWithTaxInCents !== null) {
        taxAmountOverride.value = formatCentsAsAmount(totalWithTaxInCents - withoutTaxInCents);
    }
};

const catalogueLinkDefaultValues = computed<{ value: number; label: string }[]>(() => {
    const link = catalogueLink.value;
    return link?.id ? [{ value: link.id, label: link.label ?? '' }] : [];
});

const catalogueLinkSelectKey = ref<number>(0);
const catalogueResults = ref<
    { label: string; id: number; value: number; itemType: string; icon: string }[]
>([]);
const isSearchingCatalogue = ref<boolean>(false);
let catalogueSearchAbortController: AbortController | null = null;

const abortCatalogueSearch = (): void => {
    if (catalogueSearchAbortController) {
        catalogueSearchAbortController.abort();
        catalogueSearchAbortController = null;
    }
};

const handleCatalogueFocus = (): void => {
    catalogueResults.value = [...catalogueResults.value];
};

const handleCatalogueSearch = async (query: string): Promise<void> => {
    if (!query) {
        abortCatalogueSearch();
        catalogueResults.value = [];
        isSearchingCatalogue.value = false;
        return;
    }

    abortCatalogueSearch();

    const controller = new AbortController();
    catalogueSearchAbortController = controller;

    isSearchingCatalogue.value = true;
    await nextTick();

    if (catalogueSearchAbortController !== controller) {
        return;
    }

    catalogueResults.value = [];

    try {
        const searchResponse = (await search({
            query,
            type: ['item.product', 'item.service'],
            signal: controller.signal,
        })) as { data: { data?: SearchResult[] } };
        const data = searchResponse.data.data;

        if (catalogueSearchAbortController !== controller) {
            return;
        }

        catalogueResults.value = (data ?? []).map((result: SearchResult) => ({
            label: result.name || result.number || '',
            id: result.object.id,
            value: result.object.id,
            itemType: result.object.type === 'item.service' ? 'service' : 'item',
            icon: 'obj-slsy-product',
        }));
    } catch {
        if (catalogueSearchAbortController !== controller) {
            return;
        }
        catalogueResults.value = [];
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            icon: true,
            text: $t.__('ocr.OcrVerificationForm.error_generic'),
            duration: 5000,
        });
    } finally {
        if (catalogueSearchAbortController === controller) {
            catalogueSearchAbortController = null;
            isSearchingCatalogue.value = false;
        }
    }
};

const getUpdatedProductData = async (
    itemId: number,
): Promise<{
    name: string;
    accountingPurchaseCodeId: number | null;
    itemType: 'item' | 'service';
}> => {
    const { data } = (await Item.getItemById(itemId)) as ItemApiResponse;
    const itemType: 'item' | 'service' = data.type === 'service' ? 'service' : 'item';
    return {
        name: data.name,
        accountingPurchaseCodeId: data.accounting_purchase_code_id,
        itemType,
    };
};

let currentItemUpdateHandler: CatalogueItemUpdateHandler | null = null;

const cleanupItemUpdateHandler = (): void => {
    if (currentItemUpdateHandler) {
        EventBus.off(CatalogueItemEvent.UPDATED, currentItemUpdateHandler);
        currentItemUpdateHandler = null;
    }
};

const createCatalogueItem = (type: string): void => {
    cleanupItemUpdateHandler();

    const handleItemCreated: CatalogueItemUpdateHandler = (payload) => {
        const itemId = payload.item?.id;
        if (!itemId) {
            cleanupItemUpdateHandler();
            return;
        }

        void (async () => {
            try {
                const { name, itemType } = await getUpdatedProductData(itemId);
                const newLink: NonNullable<OcrProduct['catalogueLink']> = {
                    id: itemId,
                    label: name,
                    value: itemId,
                    itemType,
                };
                catalogueLink.value = newLink;
                emit('catalogue-link-update', newLink, props.index);
                catalogueLinkSelectKey.value++;
            } catch {
                $pushToast({
                    type: CompassEnums.ToastType.ERROR,
                    icon: true,
                    text: $t.__('ocr.OcrVerificationForm.error_generic'),
                    duration: 5000,
                });
            } finally {
                cleanupItemUpdateHandler();
            }
        })();
    };

    currentItemUpdateHandler = handleItemCreated;
    EventBus.on(CatalogueItemEvent.UPDATED, handleItemCreated);

    window.catalogueItem.edit(type, undefined, false, '', {
        preventRelocate: true,
        hideAddAnother: true,
    });
};

const catalogueActions: { label: string; callback: () => void }[] = [
    {
        label: $t.__('ocr.OcrVerificationProductsTable.add_product'),
        callback: () => createCatalogueItem('item'),
    },
    {
        label: $t.__('ocr.OcrVerificationProductsTable.add_service'),
        callback: () => createCatalogueItem('service'),
    },
];

const onEdit = (): void => {
    const itemId = catalogueLink.value?.id;
    if (!itemId) {
        return;
    }

    cleanupItemUpdateHandler();

    const handleItemUpdate: CatalogueItemUpdateHandler = () => {
        void (async () => {
            try {
                const { name, accountingPurchaseCodeId } = await getUpdatedProductData(itemId);
                if (name) {
                    emit('product-updated', itemId, name, accountingPurchaseCodeId);
                }
            } catch {
                $pushToast({
                    type: CompassEnums.ToastType.ERROR,
                    icon: true,
                    text: $t.__('ocr.OcrVerificationForm.error_generic'),
                    duration: 5000,
                });
            } finally {
                cleanupItemUpdateHandler();
            }
        })();
    };

    currentItemUpdateHandler = handleItemUpdate;
    EventBus.on(CatalogueItemEvent.UPDATED, handleItemUpdate);

    const type = catalogueLink.value?.itemType ?? 'item';
    window.catalogueItem.edit(type, itemId, false, '', {
        preventRelocate: true,
        hideAddAnother: true,
    });
};

onBeforeUnmount(() => {
    abortCatalogueSearch();
    cleanupItemUpdateHandler();
});

return (_ctx: any,_cache: any) => {
  const _component_CompassInput = _resolveComponent("CompassInput")!
  const _component_CompassFormItem = _resolveComponent("CompassFormItem")!
  const _component_CompassTableBodyCell = _resolveComponent("CompassTableBodyCell")!
  const _component_CompassDropdownOption = _resolveComponent("CompassDropdownOption")!
  const _component_CompassSelect = _resolveComponent("CompassSelect")!
  const _component_CompassAutoComplete = _resolveComponent("CompassAutoComplete")!
  const _component_CompassIconButton = _resolveComponent("CompassIconButton")!
  const _component_CompassTooltip = _resolveComponent("CompassTooltip")!
  const _component_CompassTableRow = _resolveComponent("CompassTableRow")!
  const _component_CompassDatePicker = _resolveComponent("CompassDatePicker")!
  const _component_CompassTableBody = _resolveComponent("CompassTableBody")!
  const _component_CompassTable = _resolveComponent("CompassTable")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_CompassTableRow, {
      class: _normalizeClass({ 'ocr-verification-products-table__row--not-expandable': !__props.canExpand }),
      expanded: __props.isExpanded,
      "onUpdate:expanded": _cache[12] || (_cache[12] = (expanded) => emit('toggle-expand', props.index, expanded))
    }, {
      default: _withCtx(() => [
        _createVNode(_component_CompassTableBodyCell, {
          maxWidth: "200px",
          minWidth: "200px"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_CompassFormItem, {
              label: "",
              error: _unref(descriptionError)
            }, {
              default: _withCtx(() => [
                _createVNode(_component_CompassInput, {
                  modelValue: _unref(description),
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(description) ? (description).value = $event : null)),
                  class: "ocr-verification-products-table__input",
                  disabled: props.disabled,
                  error: !!_unref(descriptionError)
                }, null, 8 /* PROPS */, ["modelValue", "disabled", "error"])
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["error"])
          ]),
          _: 1 /* STABLE */
        }),
        (props.isLineItemsEnabled)
          ? (_openBlock(), _createBlock(_component_CompassTableBodyCell, {
              key: 0,
              maxWidth: "80px",
              minWidth: "80px"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_CompassInput, {
                  modelValue: _unref(quantity),
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(quantity) ? (quantity).value = $event : null)),
                  class: "ocr-verification-products-table__input",
                  disabled: props.disabled,
                  placeholder: "-",
                  type: _ctx.CompassEnums.InputType.NUMBER
                }, null, 8 /* PROPS */, ["modelValue", "disabled", "type"])
              ]),
              _: 1 /* STABLE */
            }))
          : _createCommentVNode("v-if", true),
        _createVNode(_component_CompassTableBodyCell, {
          maxWidth: "140px",
          minWidth: "140px"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_CompassFormItem, {
              label: "",
              error: _unref(priceWithoutTaxError)
            }, {
              default: _withCtx(() => [
                _createVNode(_component_CompassInput, {
                  modelValue: _unref(priceWithoutTax),
                  "onUpdate:modelValue": [
                    _cache[2] || (_cache[2] = ($event: any) => (_isRef(priceWithoutTax) ? (priceWithoutTax).value = $event : null)),
                    onPriceInput
                  ],
                  class: "ocr-verification-products-table__input",
                  disabled: props.disabled,
                  error: !!_unref(priceWithoutTaxError),
                  inputmode: "decimal",
                  onBlur: formatPriceOnBlur
                }, _createSlots({ _: 2 /* DYNAMIC */ }, [
                  (props.currencySymbol)
                    ? {
                        name: "suffix",
                        fn: _withCtx(() => [
                          _createTextVNode(_toDisplayString(props.currencySymbol), 1 /* TEXT */)
                        ]),
                        key: "0"
                      }
                    : undefined
                ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["modelValue", "disabled", "error"])
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["error"])
          ]),
          _: 1 /* STABLE */
        }),
        _createVNode(_component_CompassTableBodyCell, {
          maxWidth: "110px",
          minWidth: "110px"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_CompassFormItem, {
              label: "",
              error: _unref(taxRateIdError)
            }, {
              default: _withCtx(() => [
                _createVNode(_component_CompassSelect, {
                  modelValue: _unref(taxRateId),
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_isRef(taxRateId) ? (taxRateId).value = $event : null)),
                  class: "ocr-verification-products-table__input",
                  disabled: props.disabled,
                  filterable: "",
                  placeholder: _unref($t).__('ocr.OcrVerificationForm.select_rate'),
                  error: !!_unref(taxRateIdError)
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(compliantTaxRates.value, (item) => {
                      return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
                        key: item.id,
                        label: _unref(taxLabel)(item),
                        value: item.id
                      }, null, 8 /* PROPS */, ["label", "value"]))
                    }), 128 /* KEYED_FRAGMENT */))
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "disabled", "placeholder", "error"])
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["error"])
          ]),
          _: 1 /* STABLE */
        }),
        (props.isLineItemsEnabled)
          ? (_openBlock(), _createBlock(_component_CompassTableBodyCell, {
              key: 1,
              maxWidth: "140px",
              minWidth: "140px"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_CompassSelect, {
                  modelValue: _unref(unit),
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (_isRef(unit) ? (unit).value = $event : null)),
                  class: "ocr-verification-products-table__input",
                  disabled: props.disabled,
                  filterable: "",
                  placeholder: _unref($t).__('ocr.OcrVerificationProductsTable.select_unit')
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_CompassDropdownOption, {
                      value: null,
                      label: _unref($t).__('common.labels.none')
                    }, null, 8 /* PROPS */, ["label"]),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.units, (unitItem) => {
                      return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
                        key: unitItem.id,
                        label: unitItem.label,
                        value: unitItem.id
                      }, null, 8 /* PROPS */, ["label", "value"]))
                    }), 128 /* KEYED_FRAGMENT */))
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "disabled", "placeholder"])
              ]),
              _: 1 /* STABLE */
            }))
          : _createCommentVNode("v-if", true),
        _createVNode(_component_CompassTableBodyCell, {
          maxWidth: "140px",
          minWidth: "140px"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_CompassInput, {
              class: "ocr-verification-products-table__input",
              modelValue: taxAmount.value,
              disabled: _ctx.lockedByEinvoicing,
              inputmode: "decimal",
              "onUpdate:modelValue": onTaxAmountInput,
              onBlur: formatTaxAmountOverrideOnBlur
            }, _createSlots({ _: 2 /* DYNAMIC */ }, [
              (props.currencySymbol)
                ? {
                    name: "suffix",
                    fn: _withCtx(() => [
                      _createTextVNode(_toDisplayString(props.currencySymbol), 1 /* TEXT */)
                    ]),
                    key: "0"
                  }
                : undefined
            ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["modelValue", "disabled"])
          ]),
          _: 1 /* STABLE */
        }),
        _createVNode(_component_CompassTableBodyCell, {
          maxWidth: "140px",
          minWidth: "140px"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_CompassInput, {
              class: "ocr-verification-products-table__input",
              modelValue: amountIncludingTax.value,
              disabled: _ctx.lockedByEinvoicing,
              inputmode: "decimal",
              "onUpdate:modelValue": onAmountIncludingTaxInput,
              onBlur: formatAmountIncludingTaxOverrideOnBlur
            }, _createSlots({ _: 2 /* DYNAMIC */ }, [
              (props.currencySymbol)
                ? {
                    name: "suffix",
                    fn: _withCtx(() => [
                      _createTextVNode(_toDisplayString(props.currencySymbol), 1 /* TEXT */)
                    ]),
                    key: "0"
                  }
                : undefined
            ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["modelValue", "disabled"])
          ]),
          _: 1 /* STABLE */
        }),
        (props.isLineItemsEnabled)
          ? (_openBlock(), _createBlock(_component_CompassTableBodyCell, {
              key: 2,
              maxWidth: "260px",
              minWidth: "260px"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_1, [
                  (_openBlock(), _createBlock(_component_CompassAutoComplete, {
                    key: catalogueLinkSelectKey.value,
                    modelValue: _unref(catalogueLink),
                    "onUpdate:modelValue": [
                      _cache[5] || (_cache[5] = ($event: any) => (_isRef(catalogueLink) ? (catalogueLink).value = $event : null)),
                      _cache[6] || (_cache[6] = 
                        (value) =>
                            emit('catalogue-link-update', value, props.index)
                    )
                    ],
                    class: "ocr-verification-products-table__input",
                    defaultValues: catalogueLinkDefaultValues.value,
                    disabled: props.disabled,
                    results: catalogueResults.value,
                    loading: isSearchingCatalogue.value,
                    triggerAfter: 0,
                    menuClass: "ocr-form__select-fit-dropdown",
                    placeholder: _unref($t).__('common.actions.search'),
                    noResultsLabel: _unref($t).__('common.labels.no_result'),
                    actions: isSearchingCatalogue.value ? [] : catalogueActions,
                    onUpdateSearch: handleCatalogueSearch,
                    onFocus: handleCatalogueFocus
                  }, null, 8 /* PROPS */, ["modelValue", "defaultValues", "disabled", "results", "loading", "placeholder", "noResultsLabel", "actions"])),
                  _createVNode(_component_CompassTooltip, {
                    content: _unref($t).__('ocr.OcrVerificationProductsTable.update_product'),
                    disabled: !_unref(catalogueLink)
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_CompassIconButton, {
                        icon: "edit",
                        disabled: props.disabled,
                        size: _ctx.CompassEnums.IconButtonSize.MEDIUM,
                        class: _normalizeClass({
                            'ocr-verification-products-table__catalogue-cell__edit--hidden':
                                !_unref(catalogueLink),
                        }),
                        onClick: onEdit
                      }, null, 8 /* PROPS */, ["disabled", "size", "class"])
                    ]),
                    _: 1 /* STABLE */
                  }, 8 /* PROPS */, ["content", "disabled"])
                ])
              ]),
              _: 1 /* STABLE */
            }))
          : _createCommentVNode("v-if", true),
        _createVNode(_component_CompassTableBodyCell, {
          maxWidth: "200px",
          minWidth: "200px"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_CompassFormItem, {
              label: "",
              error: _unref(accountingCodeError)
            }, {
              default: _withCtx(() => [
                _createVNode(_component_CompassSelect, {
                  modelValue: _unref(accountingCode),
                  "onUpdate:modelValue": [
                    _cache[7] || (_cache[7] = ($event: any) => (_isRef(accountingCode) ? (accountingCode).value = $event : null)),
                    _cache[8] || (_cache[8] = 
                        (value) => emit('accounting-code-update', value, props.index)
                    )
                  ],
                  class: "ocr-verification-products-table__input",
                  disabled: props.disabled,
                  filterable: "",
                  placeholder: _unref($t).__('ocr.OcrVerificationForm.select_code'),
                  error: !!_unref(accountingCodeError)
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_CompassDropdownOption, {
                      value: null,
                      label: _unref($t).__('common.labels.none')
                    }, null, 8 /* PROPS */, ["label"]),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.accountingCodes, (item) => {
                      return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
                        key: item.id,
                        label: `${item.code} - ${item.label}`,
                        value: item.id
                      }, null, 8 /* PROPS */, ["label", "value"]))
                    }), 128 /* KEYED_FRAGMENT */))
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "disabled", "placeholder", "error"])
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["error"])
          ]),
          _: 1 /* STABLE */
        }),
        (showAnalyticCodeColumn.value)
          ? (_openBlock(), _createBlock(_component_CompassTableBodyCell, {
              key: 3,
              maxWidth: "200px",
              minWidth: "200px"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_CompassInput, {
                  modelValue: _unref(analyticCode),
                  "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => (_isRef(analyticCode) ? (analyticCode).value = $event : null)),
                  class: "ocr-verification-products-table__input",
                  disabled: props.disabled,
                  placeholder: _unref($t).__('ocr.OcrVerificationProductsTable.analytic_code')
                }, null, 8 /* PROPS */, ["modelValue", "disabled", "placeholder"])
              ]),
              _: 1 /* STABLE */
            }))
          : _createCommentVNode("v-if", true),
        _createVNode(_component_CompassTableBodyCell, { idColumn: "delete" }, {
          default: _withCtx(() => [
            _createVNode(_component_CompassTooltip, {
              content: _unref($t).__('common.actions.duplicate'),
              arrow: false
            }, {
              default: _withCtx(() => [
                _createVNode(_component_CompassIconButton, {
                  icon: "duplicate",
                  disabled: _ctx.lockedByEinvoicing,
                  size: _ctx.CompassEnums.IconButtonSize.MEDIUM,
                  onClick: _cache[10] || (_cache[10] = ($event: any) => (emit('request-duplicate', props.index)))
                }, null, 8 /* PROPS */, ["disabled", "size"])
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["content"]),
            (__props.canDelete)
              ? (_openBlock(), _createBlock(_component_CompassTooltip, {
                  key: 0,
                  content: _unref($t).__('common.actions.delete'),
                  arrow: false
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_CompassIconButton, {
                      icon: "bin",
                      disabled: _ctx.lockedByEinvoicing,
                      variant: _ctx.CompassEnums.IconButtonVariant.DESTRUCTIVE,
                      size: _ctx.CompassEnums.IconButtonSize.MEDIUM,
                      onClick: _cache[11] || (_cache[11] = ($event: any) => (emit('request-delete', props.index)))
                    }, null, 8 /* PROPS */, ["disabled", "variant", "size"])
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["content"]))
              : _createCommentVNode("v-if", true)
          ]),
          _: 1 /* STABLE */
        })
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["class", "expanded"]),
    _createVNode(_component_CompassTableRow, {
      visible: __props.isExpanded && __props.canExpand,
      subrow: ""
    }, {
      subrow: _withCtx(() => [
        _createVNode(_component_CompassTable, { displayEmptyExpandableColumn: "" }, {
          default: _withCtx(() => [
            _createVNode(_component_CompassTableBody, null, {
              default: _withCtx(() => [
                _createVNode(_component_CompassTableRow, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_CompassTableBodyCell, null, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_2, [
                          _createElementVNode("div", _hoisted_3, [
                            _createVNode(_component_CompassFormItem, {
                              label: _unref($t).__('ocr.OcrVerificationForm.stock_entry_date')
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_CompassDatePicker, {
                                  modelValue: _unref(stockOutDate) ?? '',
                                  disabled: props.disabled,
                                  formatDateDisplayed: props.formatDateDisplayed,
                                  "onUpdate:modelValue": _cache[13] || (_cache[13] = 
                                                (value) => (stockOutDate.value = value)
                                            )
                                }, null, 8 /* PROPS */, ["modelValue", "disabled", "formatDateDisplayed"])
                              ]),
                              _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["label"]),
                            _createVNode(_component_CompassFormItem, {
                              label: _unref($t).__('ocr.OcrVerificationForm.warehouse')
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_CompassSelect, {
                                  modelValue: _unref(warehouseId),
                                  "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => (_isRef(warehouseId) ? (warehouseId).value = $event : null)),
                                  disabled: props.disabled,
                                  filterable: "",
                                  placeholder: "-"
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_CompassDropdownOption, {
                                      label: "-",
                                      value: ""
                                    }),
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.warehouses, (warehouse) => {
                                      return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
                                        key: warehouse.id,
                                        label: warehouse.label,
                                        value: warehouse.id
                                      }, null, 8 /* PROPS */, ["label", "value"]))
                                    }), 128 /* KEYED_FRAGMENT */))
                                  ]),
                                  _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["modelValue", "disabled"])
                              ]),
                              _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["label"])
                          ]),
                          (isSerializedStock.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                _createVNode(_component_CompassFormItem, {
                                  label: _unref($t).__('ocr.OcrVerificationForm.serial_numbers')
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_CompassInput, {
                                      modelValue: _unref(serialNumbers),
                                      "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => (_isRef(serialNumbers) ? (serialNumbers).value = $event : null)),
                                      type: _ctx.CompassEnums.InputType.TEXTAREA,
                                      disabled: props.disabled,
                                      placeholder: 
                                                _unref($t).__('ocr.OcrVerificationForm.serial_numbers')
                                            
                                    }, null, 8 /* PROPS */, ["modelValue", "type", "disabled", "placeholder"]),
                                    _createElementVNode("span", _hoisted_5, _toDisplayString(_unref($t).__(
                                                    'ocr.OcrVerificationForm.stock.serialized.info',
                                                )), 1 /* TEXT */)
                                  ]),
                                  _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["label"])
                              ]))
                            : _createCommentVNode("v-if", true)
                        ])
                      ]),
                      _: 1 /* STABLE */
                    })
                  ]),
                  _: 1 /* STABLE */
                })
              ]),
              _: 1 /* STABLE */
            })
          ]),
          _: 1 /* STABLE */
        })
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["visible"])
  ], 64 /* STABLE_FRAGMENT */))
}
}

})